/**
 * Credit Workflow Tab for NCR Edit
 *
 * The CUSTOMER initiates the credit (amount, reason), the SUPPLIER enters the
 * credit note (number, amount, issue date, debit note raised, document), the
 * CUSTOMER confirms the credit was received; a rejected note goes back for
 * amendment (Helix addition, kept). The customer's initiate step was dropped by
 * the spec flow (QUALITY-NCR-REVIEW, 2 Oct 2026) and is restored (19 Oct 2026).
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  CheckCircle2, CreditCard, XCircle, Upload, Download, Clock, Loader2,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';
import { ProcessStepper, CREDIT_STEPS, creditStep } from './ProcessStepper';
import FileDownloadLink from '../../components/common/FileDownloadLink';
import { daysWaitingText } from '../../components/quality/ncrPlate';
import { todayLocalISO } from './localDate';
import { InitiateCreditForm, type InitiateCreditData } from './CustomerProcessRequests';

// =========================================================================
// Credit Workflow Status Stepper
// =========================================================================

/** T8: shared step boxes, plus the rework note when the customer sent it back. */
function CreditStatusStepper({ status }: { status?: string }) {
  const isRejected = status === 'rejected';
  return (
    <div className="space-y-2">
      <ProcessStepper steps={CREDIT_STEPS} current={creditStep(status)} returned={isRejected} />
      {isRejected && (
        <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
          <XCircle className="h-4 w-4" />
          The customer sent the credit note back
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Supplier: submit / re-submit the credit note (document + number required)
// =========================================================================

function SubmitCreditNoteForm({ saving, heading, initialAmount, onSubmit }: {
  saving: boolean;
  heading: string;
  /** Starts the amount at the one the customer asked for (or last submitted). */
  initialAmount?: string | null;
  onSubmit: (data: FormData) => void;
}) {
  const [form, setForm] = useState({
    credit_note_reference: '', credit_issue_date: '', supplier_notes: '',
    credit_amount: initialAmount ?? '', debit_note_raised: false,
  });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.credit_note_reference.trim()) { setError('Enter the credit note number.'); return; }
    if (!form.credit_amount) { setError('Enter the credit amount.'); return; }
    setError(null);
    const fd = new FormData();
    fd.append('credit_note_reference', form.credit_note_reference.trim());
    if (form.credit_issue_date) fd.append('credit_issue_date', form.credit_issue_date);
    fd.append('credit_amount', form.credit_amount);
    fd.append('debit_note_raised', form.debit_note_raised ? 'true' : 'false');
    if (form.supplier_notes) fd.append('supplier_notes', form.supplier_notes);
    // E19 (29 Sep 2026): the document is optional, as in the spec and legacy.
    if (file) fd.append('credit_note_document', file);
    onSubmit(fd);
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium text-sm">{heading}</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Credit Note Number <span className="text-red-500">*</span></Label>
          <Input
            value={form.credit_note_reference}
            onChange={e => setForm(f => ({ ...f, credit_note_reference: e.target.value }))}
            placeholder="Credit note number"
          />
        </div>
        <div className="space-y-2">
          <Label>Issue Date</Label>
          <Input
            type="date"
            value={form.credit_issue_date}
            onChange={e => setForm(f => ({ ...f, credit_issue_date: e.target.value }))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Credit Amount <span className="text-red-500">*</span></Label>
          <Input
            type="number"
            step="0.01"
            value={form.credit_amount}
            onChange={e => setForm(f => ({ ...f, credit_amount: e.target.value }))}
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <Label>Debit Note Raised</Label>
          <select
            value={form.debit_note_raised ? 'yes' : 'no'}
            onChange={e => setForm(f => ({ ...f, debit_note_raised: e.target.value === 'yes' }))}
            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Credit Note Document <span className="font-normal text-muted-foreground">(optional)</span></Label>
        {/* Customer review 11 Sep (drag and drop everywhere). */}
        <FileDropZone
          size="compact"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
          hint={file ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></> : 'PDF, image, Word or Excel'}
          onFiles={(files) => setFile(files[0])}
          data-testid="ncr-credit-note-drop-zone"
        />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.supplier_notes}
          onChange={e => setForm(f => ({ ...f, supplier_notes: e.target.value }))}
          rows={2}
          placeholder="Optional notes..."
        />
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1"><XCircle className="h-4 w-4" />{error}</p>
      )}
      <Button onClick={handleSubmit} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Submit Credit Note
      </Button>
    </div>
  );
}

// =========================================================================
// Credit Workflow Tab
// =========================================================================

export interface CreditWorkflowTabProps {
  ncr: NCRType;
  isCustomer: boolean;
  isSupplier: boolean;
  saving: boolean;
  /** The customer's first step: initiate with an amount and reason. */
  onInitiate?: (data: InitiateCreditData) => void;
  onSubmitNote: (data: FormData) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
}

export function CreditWorkflowTab({ ncr, isCustomer, isSupplier, saving, onInitiate, onSubmitNote, onUpdateStatus }: CreditWorkflowTabProps) {
  const { formatDate } = useFormatters();
  // Spec: Amount Confirmed starts as the supplier's credit amount (it was only
  // a placeholder, so confirming without typing sent nothing). null = not yet
  // typed, so the amount follows the process after a refetch.
  const [approvalForm, setApprovalForm] = useState<{
    credit_amount_confirmed: string | null;
    customer_credit_reference: string;
    approval_notes: string;
    received_date: string;
  }>({
    credit_amount_confirmed: null,
    customer_credit_reference: '', approval_notes: '',
    received_date: todayLocalISO(),
  });

  const process = ncr.credit_process;
  const confirmedAmount = approvalForm.credit_amount_confirmed ?? process?.credit_amount ?? '';

  if (!ncr.is_credit_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Credit is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  // A legacy NCR confirmed as credited in one step has no process row.
  if (!process && ncr.credit_completed) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Credit received</h3>
              {ncr.credit_date && (
                <p className="text-sm text-green-600">Received on {formatDate(ncr.credit_date)}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no process exists yet, the customer initiates it
  if (!process) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <CreditStatusStepper />
          {/* E2/E7 (29 Sep 2026): credit runs alongside collection, so the
              customer can ask for it while the goods are still out. */}
          {isCustomer ? (
            onInitiate && <InitiateCreditForm saving={saving} onInitiate={onInitiate} />
          ) : (
            <p className="text-sm text-sky-700">Waiting for the customer to initiate the credit process.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Process exists - show status stepper and actions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Credit</span>
          {daysWaitingText(ncr.days_waiting?.credit) && (
            <span className="text-sm font-normal tabular-nums text-muted-foreground">
              Credit: {daysWaitingText(ncr.days_waiting?.credit)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <CreditStatusStepper status={process.status} />

        {/* Process details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Requested by:</span>{' '}
            <span className="font-medium">{process.requested_by_name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Requested:</span>{' '}
            <span className="font-medium">{formatDate(process.requested_date)}</span>
          </div>
          {process.credit_amount && (
            <div>
              <span className="text-muted-foreground">Amount:</span>{' '}
              <span className="font-medium">{process.credit_currency} {parseFloat(process.credit_amount).toFixed(2)}</span>
            </div>
          )}
          {process.credit_amount_confirmed && (
            <div>
              <span className="text-muted-foreground">Confirmed amount:</span>{' '}
              <span className="font-medium">{process.credit_currency} {parseFloat(process.credit_amount_confirmed).toFixed(2)}</span>
            </div>
          )}
          {process.credit_reason && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Reason:</span>{' '}
              <span className="font-medium">{process.credit_reason}</span>
            </div>
          )}
          {/* One credit number: the supplier's credit note number, or the
              credit/debit number an older record carries instead. */}
          {(process.credit_note_reference || process.credit_debit_number) && (
            <div>
              <span className="text-muted-foreground">Credit note no:</span>{' '}
              <span className="font-medium">{process.credit_note_reference || process.credit_debit_number}</span>
            </div>
          )}
          {/* CR-7482: fetched through the API client, never linked directly —
              the raw storage URL is a 300s presigned S3 link, and a plain
              anchor to the endpoint sends no auth token. */}
          {process.credit_note_document_url && (
            <div>
              <span className="text-muted-foreground">Credit note:</span>{' '}
              <FileDownloadLink
                url={process.credit_note_document_url}
                fallbackName="credit-note"
                icon={<Download className="h-3.5 w-3.5" />}
                className="font-medium text-primary inline-flex items-center gap-1 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
              >
                View document
              </FileDownloadLink>
            </div>
          )}
          {/* The supplier answers this with the credit note; not before. */}
          {process.status !== 'requested' && (
            <div>
              <span className="text-muted-foreground">Debit note raised:</span>{' '}
              <span className="font-medium">{process.debit_note_raised ? 'Yes' : 'No'}</span>
            </div>
          )}
          {process.customer_credit_reference && (
            <div>
              <span className="text-muted-foreground">Customer ref:</span>{' '}
              <span className="font-medium">{process.customer_credit_reference}</span>
            </div>
          )}
        </div>

        {/* Notes display */}
        {(process.customer_notes || process.supplier_notes || process.approval_notes) && (
          <div className="space-y-2 border-t pt-4">
            {process.customer_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Customer notes:</span> {process.customer_notes}</div>
            )}
            {process.supplier_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Supplier notes:</span> {process.supplier_notes}</div>
            )}
            {process.approval_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Approval notes:</span> {process.approval_notes}</div>
            )}
          </div>
        )}

        {/* Actions based on current status */}

        {/* Supplier: submit the credit note (requested -> note_submitted) */}
        {process.status === 'requested' && isSupplier && (
          <SubmitCreditNoteForm saving={saving} heading="Submit Credit Note" initialAmount={process.credit_amount} onSubmit={onSubmitNote} />
        )}

        {/* Customer: waiting for the supplier's credit note */}
        {process.status === 'requested' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Awaiting the supplier to upload the credit note.
          </div>
        )}

        {/* Customer: approve or reject the submitted credit note */}
        {/* Round 4: a legacy row left at under_review takes the same answer. */}
        {['note_submitted', 'under_review'].includes(process.status) && isCustomer && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Credit confirmation</h4>
            <div className="space-y-2 max-w-xs">
              <Label>Date Credit Received <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                required
                max={todayLocalISO()}
                value={approvalForm.received_date}
                onChange={e => setApprovalForm(f => ({ ...f, received_date: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Confirmed Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={confirmedAmount}
                  onChange={e => setApprovalForm(f => ({ ...f, credit_amount_confirmed: e.target.value }))}
                  placeholder={process.credit_amount || '0.00'}
                />
              </div>
              <div className="space-y-2">
                <Label>Customer Reference</Label>
                <Input
                  value={approvalForm.customer_credit_reference}
                  onChange={e => setApprovalForm(f => ({ ...f, customer_credit_reference: e.target.value }))}
                  placeholder="Your internal reference"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={approvalForm.approval_notes}
                onChange={e => setApprovalForm(f => ({ ...f, approval_notes: e.target.value }))}
                placeholder="Approval or rejection notes..."
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onUpdateStatus('approved', {
                received_date: approvalForm.received_date,
                credit_amount_confirmed: confirmedAmount ? parseFloat(confirmedAmount) : undefined,
                customer_credit_reference: approvalForm.customer_credit_reference,
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving || !approvalForm.received_date} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Confirm credit received
              </Button>
              <Button variant="destructive" onClick={() => onUpdateStatus('rejected', {
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        )}

        {/* Supplier: waiting for customer approval */}
        {['note_submitted', 'under_review'].includes(process.status) && isSupplier && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Credit note submitted. Awaiting customer approval.
          </div>
        )}

        {/* Supplier: amend and re-submit after a rejection */}
        {process.status === 'rejected' && isSupplier && (
          <SubmitCreditNoteForm saving={saving} heading="Amend &amp; Re-submit Credit Note" initialAmount={process.credit_amount} onSubmit={onSubmitNote} />
        )}

        {/* Customer: waiting for the supplier to resubmit */}
        {process.status === 'rejected' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Rejected. Awaiting the supplier to amend and resubmit.
          </div>
        )}

        {process.status === 'processed' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Credit received</h3>
              {process.received_date && (
                <p className="text-sm text-green-600">Received on {formatDate(process.received_date)}</p>
              )}
              {process.processed_date && (
                <p className="text-sm text-green-600">
                  Processed on {formatDate(process.processed_date)}
                  {process.processed_by_name && ` by ${process.processed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
